(() => {
  if (window.ItoCartSync) return;

  const views = new Map();
  let queue = Promise.resolve();
  let pending = 0;
  const errorMessage = () => window.cartStrings?.error || 'Unable to update your cart. Please try again.';
  const root = () => window.Shopify?.routes?.root || '/';
  const cartPath = () => window.routes?.cart_url || `${root()}cart`;
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(`ito:cart-${name}`, { detail }));

  async function request(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        ...options,
        signal: controller.signal,
      });
      const result = await response.json();
      if (!response.ok || result?.status >= 400) {
        throw new Error(result?.description || result?.message || errorMessage());
      }
      if (!result) throw new Error(errorMessage());
      return result;
    } finally {
      clearTimeout(timer);
    }
  }

  function enqueue(work) {
    pending += 1;
    emit('busy', { busy: true });
    const task = queue.then(async () => {
      emit('error', { message: '' });
      try {
        return await work();
      } catch (error) {
        emit('error', { message: error.message || errorMessage() });
        throw error;
      } finally {
        pending -= 1;
        emit('busy', { busy: pending > 0 });
      }
    });
    // A failed request must not prevent later cart operations.
    queue = task.catch(() => {});
    return task;
  }

  function apply(cart, sections) {
    if (!Number.isInteger(cart?.item_count)) throw new Error(errorMessage());
    // Validate every section before replacing either view (Shopify can return null).
    const commits = Array.from(views, ([id, prepare]) => {
      if (typeof sections?.[id] !== 'string') throw new Error(errorMessage());
      const parsed = new DOMParser().parseFromString(sections[id], 'text/html');
      return prepare(parsed, cart);
    });
    commits.forEach((commit) => commit());
    document.querySelectorAll('.cart-count').forEach((count) => {
      count.textContent = count.classList.contains('cart-count-drawer')
        ? `(${cart.item_count})`
        : cart.item_count > 0 ? String(cart.item_count) : '';
    });
    document.querySelector('header-total-price')?.updateTotal?.(cart);
    emit('updated', { cart });
    return cart;
  }

  async function readAndApply() {
    const url = new URL(cartPath(), window.location.origin);
    url.searchParams.set('sections', Array.from(views.keys()).join(','));
    const [cart, sections] = await Promise.all([
      request(`${cartPath()}.js`, { cache: 'no-store' }),
      request(url.toString(), { cache: 'no-store' }),
    ]);
    return apply(cart, sections);
  }

  async function refreshWithRetry() {
    try {
      return await readAndApply();
    } catch {
      return readAndApply();
    }
  }

  async function post(action, data, renderSections = true) {
    const sectionData = { sections: Array.from(views.keys()), sections_url: cartPath() };
    const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
    let body;
    if (data instanceof FormData) {
      body = new FormData();
      for (const [key, value] of data) body.append(key, value);
      body.delete('sections');
      body.delete('sections_url');
      if (renderSections) {
        body.set('sections', sectionData.sections.join(','));
        body.set('sections_url', sectionData.sections_url);
      }
    } else {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify({ ...data, ...(renderSections ? sectionData : {}) });
    }
    const route = window.routes?.[`cart_${action}_url`] || `${root()}cart/${action}`;
    return request(route.endsWith('.js') ? route : `${route}.js`, { method: 'POST', headers, body });
  }

  async function finishMutation(result) {
    try {
      // add.js returns added lines rather than a complete cart.
      const cart = Number.isInteger(result.item_count) ? result : await request(`${cartPath()}.js`);
      return apply(cart, result.sections);
    } catch {
      // Retry only reads: never repeat an already accepted add/remove operation.
      return refreshWithRetry();
    }
  }

  async function mutate(work) {
    let result;
    try {
      result = await work();
    } catch (error) {
      // A timeout or a failed second half of quick edit may still have changed the cart.
      await refreshWithRetry().catch(() => {});
      throw error;
    }
    return finishMutation(result);
  }

  function lineSignature(item) {
    const properties = Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b));
    return JSON.stringify([
      String(item.variant_id || item.id),
      String(item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || ''),
      properties,
    ]);
  }

  function matchingLines(cart, item) {
    const signature = lineSignature(item);
    return cart.items.filter((line) => lineSignature(line) === signature);
  }

  function matchingQuantity(cart, item) {
    return matchingLines(cart, item).reduce((quantity, line) => quantity + line.quantity, 0);
  }

  function findOriginalLine(cart, original) {
    const matches = matchingLines(cart, original);
    // Discounts can change a line key after adding another variant. Never guess
    // between multiple matching lines, or fall back to a bare variant ID.
    return matches.find((line) => line.key === original.key) || (matches.length === 1 ? matches[0] : null);
  }

  async function replaceLine(id, data) {
    const variantId = Number(data.get('id'));
    const quantity = Number(data.get('quantity'));
    if (!Number.isSafeInteger(variantId) || variantId < 1 || !Number.isInteger(quantity) || quantity < 1) {
      throw new Error('Please select an available option and a valid quantity.');
    }
    const readCart = () => request(`${cartPath()}.js`, { cache: 'no-store' });
    const before = await readCart();
    const original = before.items.find((item) => item.key === id);
    if (!original) throw new Error('Your cart has changed. Please reopen Edit Option and try again.');

    // Quantity-only changes must not remove/re-add the item or discard its properties.
    if (Number(original.variant_id) === variantId) {
      return post('change', { id: original.key, quantity });
    }

    const replacement = { id: variantId, quantity, properties: original.properties || {} };
    const sellingPlan = original.selling_plan_allocation?.selling_plan?.id;
    if (sellingPlan) replacement.selling_plan = sellingPlan;
    const originalQuantity = matchingQuantity(before, original);
    const previousQuantity = matchingQuantity(before, replacement);
    let removalRequested = false;

    try {
      // Keep the original until the complete replacement quantity is confirmed.
      await post('add', { items: [replacement] }, false);
      const afterAdd = await readCart();
      const currentOriginal = findOriginalLine(afterAdd, original);
      if (!currentOriginal || currentOriginal.quantity !== original.quantity ||
          matchingQuantity(afterAdd, original) !== originalQuantity ||
          matchingQuantity(afterAdd, replacement) !== previousQuantity + quantity) {
        throw new Error('Your cart changed while saving. Please review it and try again.');
      }
      removalRequested = true;
      return await post('change', { id: currentOriginal.key, quantity: 0 });
    } catch (error) {
      // An error response (including insufficient stock) may still have added
      // some items. Reconcile actual quantities before attempting compensation.
      let current;
      try { current = await readCart(); } catch {
        throw new Error('Unable to confirm this edit. Please refresh and check your cart before trying again.');
      }
      const currentOriginalQuantity = matchingQuantity(current, original);
      const currentReplacementQuantity = matchingQuantity(current, replacement);
      if (removalRequested && currentOriginalQuantity === originalQuantity - original.quantity &&
          currentReplacementQuantity === previousQuantity + quantity) {
        // The removal succeeded but its response was lost. Do not add again.
        return current;
      }

      const currentOriginal = findOriginalLine(current, original);
      if (currentOriginal?.quantity === original.quantity && currentOriginalQuantity === originalQuantity) {
        const addedQuantity = currentReplacementQuantity - previousQuantity;
        if (addedQuantity === 0) throw error;
        const replacements = matchingLines(current, replacement);
        if (replacements.length === 1 && addedQuantity > 0 && addedQuantity <= quantity) {
          try {
            // Restore the pre-edit quantity, including an existing merged line.
            await post('change', { id: replacements[0].key, quantity: previousQuantity }, false);
          } catch {
            // Never repeat a mutation after a timeout; check whether it applied.
          }
          const restored = await readCart().catch(() => null);
          if (restored && matchingQuantity(restored, original) === originalQuantity &&
              matchingQuantity(restored, replacement) === previousQuantity) throw error;
        }
      }
      // Ambiguous/split lines or concurrent changes must not be blindly deleted.
      throw new Error('This edit could not be completed. Please check the items and quantities in your cart before trying again.');
    }
  }

  function preserveState(scope) {
    const fields = Array.from(scope.querySelectorAll('[name="note"], [name="conditions"], [name="agree_terms"]'),
      (field) => ({ name: field.name, value: field.value, checked: field.checked }));
    const scroller = scope.querySelector('.bls-items');
    const scrollTop = scroller?.scrollTop;
    const active = scope.contains(document.activeElement) ? document.activeElement : null;
    const rowKey = active?.closest('[data-cart-line-key]')?.dataset.cartLineKey;
    const focusRoot = rowKey ? active.closest('[data-cart-line-key]') : scope;
    const controls = 'a, button, input, select, textarea, [tabindex]';
    const focusIndex = active ? Array.from(focusRoot.querySelectorAll(controls)).indexOf(active) : -1;
    return () => {
      fields.forEach(({ name, value, checked }) => {
        const field = scope.querySelector(`[name="${name}"]`);
        if (!field) return;
        field.value = value;
        if (field.type === 'checkbox') field.checked = checked;
      });
      const nextScroller = scope.querySelector('.bls-items');
      if (nextScroller && scrollTop != null) nextScroller.scrollTop = scrollTop;
      if (!active || active.isConnected) return;
      const nextRoot = rowKey
        ? Array.from(scope.querySelectorAll('[data-cart-line-key]')).find((row) => row.dataset.cartLineKey === rowKey)
        : scope;
      const next = nextRoot?.querySelectorAll(controls)[focusIndex];
      if (next) next.focus({ preventScroll: true });
      else {
        const fallback = scope.closest('[role="dialog"]') || scope;
        fallback.setAttribute('tabindex', '-1');
        fallback.focus({ preventScroll: true });
      }
    };
  }

  window.ItoCartSync = {
    preserveState,
    register(id, prepare) { views.set(id, prepare); },
    get busy() { return pending > 0; },
    refresh() { return enqueue(refreshWithRetry); },
    change(id, quantity) {
      return enqueue(() => mutate(() => post('change', { id, quantity })));
    },
    add(data) { return enqueue(() => mutate(() => post('add', data))); },
    update(data) { return enqueue(() => mutate(() => post('update', data))); },
    replace(id, data) {
      return enqueue(() => mutate(() => replaceLine(id, data)));
    },
  };
})();
